import Phaser from 'phaser';
import type { Room } from 'colyseus.js';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { getOnlineClient, resetOnlineClient } from '@/online/colyseusClient';
import { getOnlineRoom, setOnlineRoom, clearOnlineRoom } from '@/online/session';
import { getRandomUniqueNames } from '@/config/playerNames';
import { parseRoomStateView, type SeatView } from '@/online/roomState';
import { createButton, createSmallButton, setButtonEnabled, setButtonLabel } from '@/ui/buttons';
import { isMobileDevice } from '@/utils/mobile';
import { getDiscordDisplayName, setDiscordLobbyPresence, clearDiscordPresence } from '@/discord';
import { isDiscordActivity } from '@/utils/discordActivity';
import { captureError } from '@/sentry';
import { debugLog } from '@/online/debug';
import { KICK_COOLDOWN_MS } from '@/config/online';
import { getJoinUrl } from '@/online/invite';
import { makeQrSvg } from '@/online/qr';
import type { InputBinding, MultiplayerGameData } from '@/types/multiplayer';
import { getLocalSeatColor } from '@/utils/seatColors';
import { BindingFormModal, SimpleFormModal } from '@/ui/Modal';
import { loadSettings } from '@/config/settings';
import { getDeviceId } from '@/online/deviceId';
import { publishInstanceLobby, releaseInstanceLobby } from '@/online/instanceLobby';
import { trackLobbyView, trackLobbyReady, trackInviteShared } from '@/services/analytics';

export interface LobbySceneData {
  role: 'host' | 'guest';
  roomId?: string;
  name?: string;
  mode?: 'coop' | 'telephone';
}

const ROW_CORNER_RADIUS = 14;

export class LobbyScene extends Phaser.Scene {
  private role: 'host' | 'guest' = 'guest';
  private room: Room | null = null;
  private roomMode: 'coop' | 'telephone' = 'coop';
  private totalPlayers: number = 2;
  private hostSessionId: string | null = null;
  private assignedSeat: number | null = null;
  private isReady: boolean = false;
  private preserveRoomOnShutdown: boolean = false;
  private isMobile: boolean = false;
  private playerName: string = '';

  private seatsByIndex = new Map<number, SeatView>();
  private binding: InputBinding = { playerId: 0, type: 'keyboard', key: 'SPACE' };

  private titleText: Phaser.GameObjects.Text | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private copyCodeButton: Phaser.GameObjects.Container | null = null;
  private loadingText: Phaser.GameObjects.Text | null = null;
  private mainContainer: Phaser.GameObjects.Container | null = null;
  private isRoomReady: boolean = false;

  private rowBackgrounds: Array<Phaser.GameObjects.Graphics | null> = [null, null, null, null];
  private rowBounds: Array<{ x: number; y: number; width: number; height: number } | null> = [null, null, null, null];
  private rowSlotTexts: Array<Phaser.GameObjects.Text | null> = [null, null, null, null];
  private rowNameTexts: Array<Phaser.GameObjects.Text | null> = [null, null, null, null];
  private rowStatusTexts: Array<Phaser.GameObjects.Text | null> = [null, null, null, null];
  private rowActionButtons: Array<Phaser.GameObjects.Container | null> = [null, null, null, null];

  private readyButton: Phaser.GameObjects.Container | null = null;
  private startButton: Phaser.GameObjects.Container | null = null;
  private leaveButton: Phaser.GameObjects.Container | null = null;
  private modeCoopBtn: Phaser.GameObjects.Container | null = null;
  private modeTelephoneBtn: Phaser.GameObjects.Container | null = null;
  private difficultyButtons: Map<string, Phaser.GameObjects.Container> = new Map();
  private roomDifficulty: string = 'medium';
  private bindingText: Phaser.GameObjects.Text | null = null;
  private guestModeLabel: Phaser.GameObjects.Text | null = null;
  private guestDifficultyLabel: Phaser.GameObjects.Text | null = null;

  private invitePanel: Phaser.GameObjects.Rectangle | null = null;
  private inviteCodeText: Phaser.GameObjects.Text | null = null;
  private qrElement: Phaser.GameObjects.DOMElement | null = null;

  private modal: BindingFormModal | SimpleFormModal | null = null;

  constructor() {
    super({ key: 'LobbyScene' });
  }

  init(data: LobbySceneData): void {
    this.role = data.role;
    this.roomMode = data.mode ?? 'coop';
    this.totalPlayers = 1; // Dynamic - will be updated from server state
    this.playerName = data.name || getDiscordDisplayName() || (getRandomUniqueNames(1)[0] ?? 'Player');
    this.isMobile = isMobileDevice();
    this.assignedSeat = null;
    this.isReady = false;
    this.seatsByIndex.clear();
    this.preserveRoomOnShutdown = false;
    this.hostSessionId = null;
    this.isRoomReady = false;

    // Load binding from user's saved settings
    const settings = loadSettings();
    const savedBinding = settings.inputBindings?.[0];
    if (savedBinding) {
      this.binding = { ...savedBinding, playerId: 0 };
    } else if (this.isMobile) {
      this.binding = { playerId: 0, type: 'mouse', mouseButton: 0 };
    } else {
      this.binding = { playerId: 0, type: 'keyboard', key: settings.inputKey ?? 'SPACE' };
    }
  }

  create(): void {
    this.cameras.main.fadeIn(300, 5, 7, 22);
    drawArcadeBackground(this);

    this.titleText = this.add.text(GAME_WIDTH / 2 - 100, 70, 'LOBBY', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.titleLarge,
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    addTextGlow(this.titleText, THEME.colors.cyan, 15);

    // Loading state - shown until room is connected
    this.loadingText = this.add.text(GAME_WIDTH / 2 - 100, GAME_HEIGHT / 2, 'Creating lobby...', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    // Main content container - hidden until room is ready
    this.mainContainer = this.add.container(0, 0);
    this.mainContainer.setVisible(false);

    // Host controls (mode selector, difficulty) - positioned below title
    if (this.role === 'host') {
      this.createHostControls();
      if (!isDiscordActivity()) {
        this.createInvitePanel();
      }
    } else {
      this.loadingText.setText('Connecting...');
      this.createGuestControls();
    }

    // Create seat rows - positioned below mode/difficulty selectors
    this.createSeatRows(340);

    // Binding display at bottom (same for host and guest)
    const controlsX = GAME_WIDTH / 2 - 100;
    this.bindingText = this.add.text(controlsX, GAME_HEIGHT - 40, this.getBindingDescription(), {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.mainContainer?.add(this.bindingText);

    // Connect to room after UI is set up
    // Check for existing room first (e.g., returning from ResultScene)
    const existingRoom = getOnlineRoom();
    if (existingRoom) {
      this.attachRoom(existingRoom);
      if (this.role === 'host' && !isDiscordActivity()) {
        this.updateInvitePanel();
      }
    } else if (this.role === 'host') {
      void this.createHostRoom();
    } else {
      // Guest with no existing room - shouldn't happen, but handle gracefully
      this.setStatus('No room connection found');
    }

    this.statusText = this.add.text(GAME_WIDTH / 2 - 100, GAME_HEIGHT - 20, '', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    // Close Lobby button (top left, wider than standard back button)
    this.createCloseLobbyButton();

    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.modal) {
        this.modal.destroy();
        this.modal = null;
        return;
      }
      this.leaveLobby();
    });
  }

  private createCloseLobbyButton(): void {
    const x = 140;
    const y = 70;
    const width = 180;
    const height = 44;

    const container = this.add.container(x, y);
    const bg = this.add.rectangle(0, 0, width, height, THEME.colors.backgroundPanelHex, 1);
    bg.setStrokeStyle(2, THEME.colors.cyanHex);
    bg.setInteractive({ useHandCursor: true });

    const label = this.add.text(0, 0, '← CLOSE LOBBY', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.cyan,
    }).setOrigin(0.5);

    container.add([bg, label]);
    bg.on('pointerover', () => bg.setFillStyle(THEME.colors.backgroundLightHex, 1));
    bg.on('pointerout', () => bg.setFillStyle(THEME.colors.backgroundPanelHex, 1));
    bg.on('pointerdown', () => this.leaveLobby());
    // Note: Not added to mainContainer - always visible for escape during loading/errors
  }

  private async createHostRoom(): Promise<void> {
    try {
      const client = getOnlineClient();
      const room = await client.create('morse', {
        mode: this.roomMode,
        // Note: totalPlayers removed - seats are now dynamic (added on join, removed on leave)
        name: this.playerName,
        deviceId: getDeviceId(),
      });
      this.attachRoom(room);

      if (isDiscordActivity()) {
        // Publish room to instance lobby so other Discord users can auto-join
        await publishInstanceLobby({ mode: this.roomMode, roomId: room.roomId });
      } else {
        this.updateInvitePanel();
      }
    } catch (err) {
      captureError(err, { scope: 'lobbyScene', action: 'createHostRoom' });
      resetOnlineClient();
      // Show error state - hide loading text and display error
      if (this.loadingText) {
        this.loadingText.setText('Failed to create lobby');
        this.loadingText.setColor(THEME.colors.magenta);
      }
      this.setStatus('Check your connection and try again');
    }
  }

  private attachRoom(room: Room): void {
    this.room = room;
    setOnlineRoom(room);
    this.hostSessionId = null;

    // Clean up any existing listeners from previous scenes (JoinScene, GameScene, etc.)
    room.removeAllListeners?.();

    // Suppress Colyseus warning - seat_assigned is handled via state sync
    room.onMessage('seat_assigned', () => {});

    debugLog('attachRoom', { roomId: room.roomId, sessionId: room.sessionId });

    const syncFromState = (state: unknown) => {
      const parsed = parseRoomStateView(state);
      this.seatsByIndex.clear();
      this.totalPlayers = parsed.totalPlayers ?? 2;
      this.hostSessionId = parsed.hostSessionId;

      for (const seat of parsed.seats) {
        this.seatsByIndex.set(seat.seat, seat);
      }

      const mode = parsed.mode === 'telephone' ? 'telephone' : 'coop';
      this.roomMode = mode;
      this.roomDifficulty = parsed.difficulty ?? 'medium';

      // Check if we are now the host (host handoff)
      if (parsed.hostSessionId === room.sessionId) {
        this.role = 'host';
      }

      // Find our seat
      const mySeat = parsed.seats.find(s => s.ownerSessionId === room.sessionId);
      if (mySeat) {
        this.assignedSeat = mySeat.seat;
        this.isReady = mySeat.ready;
      }

      // Update Discord presence with current state
      setDiscordLobbyPresence({
        mode: this.roomMode,
        difficulty: this.roomDifficulty,
        roomId: room.roomId,
        playerCount: this.totalPlayers,
      });

      this.updateUI();
    };

    room.onStateChange((state) => syncFromState(state));
    syncFromState(room.state);

    // Show main content now that we have room state
    this.showMainContent();

    room.onMessage('host_changed', (payload: { newHostSessionId?: string }) => {
      if (payload.newHostSessionId === room.sessionId) {
        this.role = 'host';
        this.setStatus('You are now the host!');
        // Recreate host controls
        this.scene.restart({ role: 'host', mode: this.roomMode });
      }
    });

    room.onMessage('kicked', (_payload: { reason?: string }) => {
      this.preserveRoomOnShutdown = false;
      clearOnlineRoom();
      this.scene.start('KickedScene', { remainingMs: KICK_COOLDOWN_MS, roomId: room.roomId });
    });

    room.onMessage('match_starting', (payload: { startAt?: number; message?: string; difficulty?: string; mode?: string }) => {
      const { startAt, message, difficulty, mode } = payload;
      if (!startAt || !message || !difficulty) return;
      if (this.assignedSeat === null) return;

      const players = Array.from({ length: this.totalPlayers }, (_, seat) => {
        const seatView = this.seatsByIndex.get(seat);
        const binding = seat === this.assignedSeat
          ? { ...this.binding, playerId: seat }
          : { playerId: seat, type: 'keyboard' as const, key: 'SPACE' };
        return {
          playerId: seat,
          name: seatView?.name || `Player ${seat + 1}`,
          inputBinding: binding,
        };
      });

      const data: MultiplayerGameData = {
        mode: mode === 'telephone' ? 'telephone' : 'coop',
        players,
        difficulty: difficulty.toLowerCase(),
        message,
        online: {
          enabled: true,
          roomId: room.roomId,
          startAt,
          localSeatIndices: [this.assignedSeat],
        },
      };

      this.preserveRoomOnShutdown = true;
      room.removeAllListeners?.();
      setOnlineRoom(room);
      this.scene.start(data.mode === 'telephone' ? 'TelephoneGameScene' : 'CoopGameScene', data);
    });

    room.onLeave((code) => {
      debugLog('room.onLeave', { code, preserveRoomOnShutdown: this.preserveRoomOnShutdown });
      if (!this.preserveRoomOnShutdown) {
        clearOnlineRoom();
        clearDiscordPresence();
        this.scene.start(isDiscordActivity() ? 'MenuScene' : 'FriendWebPlayScene');
      }
    });

    room.onError((code, message) => {
      debugLog('room.onError', { code, message });
    });
  }

  private createHostControls(): void {
    const modeY = 160;
    const difficultyY = 240;
    const startY = GAME_HEIGHT - 100;
    const controlsX = GAME_WIDTH / 2 - 100;

    // Mode selector label (above buttons) - match LevelSelectScene style
    const modeLabel = this.add.text(controlsX - 35, modeY - 35, 'GAME TO PLAY', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.mainContainer?.add(modeLabel);

    // Create pillbox-style mode buttons
    this.modeCoopBtn = this.createModeButton(controlsX - 100, modeY, 'CO-OP', 'coop');
    this.modeTelephoneBtn = this.createModeButton(controlsX + 30, modeY, 'TELEPHONE', 'telephone');
    if (this.modeCoopBtn) this.mainContainer?.add(this.modeCoopBtn);
    if (this.modeTelephoneBtn) this.mainContainer?.add(this.modeTelephoneBtn);

    // Difficulty selector label - match LevelSelectScene style
    const diffLabel = this.add.text(controlsX - 35, difficultyY - 35, 'DIFFICULTY', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.mainContainer?.add(diffLabel);

    // Difficulty buttons - match LevelSelectScene dimensions and style
    const difficulties = ['easy', 'medium', 'hard', 'expert'];
    const diffBtnWidth = 100;
    const diffStartX = controlsX - 165;
    difficulties.forEach((diff, i) => {
      const btn = this.createDifficultyButton(diffStartX + i * 120, difficultyY, diff);
      this.difficultyButtons.set(diff, btn);
      this.mainContainer?.add(btn);
    });

    // Start button
    this.startButton = createButton(this, {
      x: controlsX,
      y: startY,
      text: 'START MATCH',
      onClick: () => this.startMatch(),
      style: 'primary',
      buttonType: 'normal',
    });
    setButtonEnabled(this.startButton, false);
    if (this.startButton) this.mainContainer?.add(this.startButton);
  }

  private createDifficultyButton(x: number, y: number, difficulty: string): Phaser.GameObjects.Container {
    const width = 100;
    const height = 40;
    const container = this.add.container(x, y);

    const bg = this.add.rectangle(0, 0, width, height, THEME.colors.backgroundPanelHex, 1);
    bg.setStrokeStyle(2, THEME.colors.borderHex);
    bg.setInteractive({ useHandCursor: true });
    bg.setName('bg');

    const label = this.add.text(0, 0, difficulty.toUpperCase(), {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    label.setName('label');

    container.add([bg, label]);
    container.setData('difficulty', difficulty);

    bg.on('pointerdown', () => {
      if (this.roomDifficulty !== difficulty) {
        this.setDifficulty(difficulty);
      }
    });

    return container;
  }

  private updateDifficultyButtons(): void {
    this.difficultyButtons.forEach((btn, diff) => {
      const isSelected = this.roomDifficulty === diff;
      const bg = btn.getByName('bg') as Phaser.GameObjects.Rectangle | null;
      const label = btn.getByName('label') as Phaser.GameObjects.Text | null;

      if (bg) {
        bg.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.borderHex);
      }
      if (label) {
        label.setColor(isSelected ? THEME.colors.text : THEME.colors.textMuted);
      }
    });
  }

  private setDifficulty(difficulty: string): void {
    if (this.role !== 'host') return;
    if (!this.room) return;
    this.roomDifficulty = difficulty;
    this.updateDifficultyButtons();
    this.room.send('set_difficulty', { difficulty });
  }

  private createModeButton(x: number, y: number, text: string, mode: 'coop' | 'telephone'): Phaser.GameObjects.Container {
    const width = 120;
    const height = 40;
    const container = this.add.container(x, y);

    const bg = this.add.rectangle(0, 0, width, height, THEME.colors.backgroundPanelHex, 1);
    bg.setStrokeStyle(2, THEME.colors.borderHex);
    bg.setInteractive({ useHandCursor: true });
    bg.setName('bg');

    const label = this.add.text(0, 0, text, {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    label.setName('label');

    container.add([bg, label]);
    container.setData('mode', mode);

    bg.on('pointerdown', () => {
      debugLog('modeButton clicked', { buttonMode: mode, currentMode: this.roomMode });
      if (this.roomMode !== mode) {
        this.setMode(mode);
      }
    });

    return container;
  }

  private updateModeButtons(): void {
    [this.modeCoopBtn, this.modeTelephoneBtn].forEach(btn => {
      if (!btn) return;
      const mode = btn.getData('mode') as 'coop' | 'telephone';
      const isSelected = this.roomMode === mode;
      const bg = btn.getByName('bg') as Phaser.GameObjects.Rectangle | null;
      const label = btn.getByName('label') as Phaser.GameObjects.Text | null;

      if (bg) {
        bg.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.borderHex);
      }
      if (label) {
        label.setColor(isSelected ? THEME.colors.text : THEME.colors.textMuted);
      }
    });
  }

  private createGuestControls(): void {
    const controlsX = GAME_WIDTH / 2 - 100;
    const modeY = 160;
    const difficultyY = 240;
    const readyY = GAME_HEIGHT - 100;

    // Mode label (read-only for guests) - match LevelSelectScene style
    const modeLabel = this.add.text(controlsX - 35, modeY - 35, 'GAME TO PLAY', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.mainContainer?.add(modeLabel);

    this.guestModeLabel = this.add.text(controlsX - 35, modeY, this.roomMode === 'telephone' ? 'TELEPHONE' : 'CO-OP', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.text,
    }).setOrigin(0.5);
    if (this.guestModeLabel) this.mainContainer?.add(this.guestModeLabel);

    // Difficulty label (read-only for guests) - match LevelSelectScene style
    const diffLabel = this.add.text(controlsX - 35, difficultyY - 35, 'DIFFICULTY', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.mainContainer?.add(diffLabel);

    this.guestDifficultyLabel = this.add.text(controlsX - 35, difficultyY, this.roomDifficulty.toUpperCase(), {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.text,
    }).setOrigin(0.5);
    if (this.guestDifficultyLabel) this.mainContainer?.add(this.guestDifficultyLabel);

    this.readyButton = createButton(this, {
      x: controlsX,
      y: readyY,
      text: 'MARK READY',
      onClick: () => this.toggleReady(),
      style: 'primary',
      buttonType: 'normal',
    });
    if (this.readyButton) this.mainContainer?.add(this.readyButton);
  }

  private getBindingDescription(): string {
    if (this.binding.type === 'keyboard') {
      return `Press ${this.binding.key} for dots and dashes`;
    } else if (this.binding.type === 'mouse') {
      return `Click mouse for dots and dashes`;
    }
    return 'Press SPACE for dots and dashes';
  }

  private updateBindingDisplay(): void {
    if (this.bindingText) {
      this.bindingText.setText(this.getBindingDescription());
    }
  }

  private createInvitePanel(): void {
    const panelX = GAME_WIDTH - 200;
    const panelY = 320;

    this.invitePanel = this.add.rectangle(panelX, panelY, 320, 340, THEME.colors.backgroundLightHex, 0.85);
    this.invitePanel.setStrokeStyle(2, THEME.colors.borderHex);
    this.mainContainer?.add(this.invitePanel);

    const inviteTitle = this.add.text(panelX, panelY - 140, 'Invite friends', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    this.mainContainer?.add(inviteTitle);

    this.inviteCodeText = this.add.text(panelX, panelY - 100, '', {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(0.5);
    if (this.inviteCodeText) this.mainContainer?.add(this.inviteCodeText);

    // Copy code button (copies just the room code)
    this.copyCodeButton = createSmallButton(this, {
      x: panelX,
      y: panelY - 60,
      text: 'COPY CODE',
      onClick: () => this.copyRoomCode(),
    });
    if (this.copyCodeButton) this.mainContainer?.add(this.copyCodeButton);

    // Copy link button (copies full URL)
    const copyLinkButton = createSmallButton(this, {
      x: panelX,
      y: panelY - 20,
      text: 'COPY LINK',
      onClick: () => this.copyInviteLink(),
    });
    this.mainContainer?.add(copyLinkButton);

    // QR code container (DOM elements can't be added to containers, hidden until room is ready)
    const qrHtml = '<div data-qr style="width:120px;height:120px;background:#fff;border-radius:8px;"></div>';
    this.qrElement = this.add.dom(panelX, panelY + 70).createFromHTML(qrHtml);
    this.qrElement.setVisible(false);  // Hidden until showMainContent is called
  }

  private updateInvitePanel(): void {
    if (!this.room || !this.inviteCodeText) return;

    this.inviteCodeText.setText(this.room.roomId);

    if (this.qrElement) {
      const qrDiv = this.qrElement.node.querySelector('[data-qr]') as HTMLDivElement | null;
      if (qrDiv) {
        const url = getJoinUrl(this.room.roomId);
        qrDiv.innerHTML = url ? makeQrSvg(url) ?? '' : '';
      }
    }
  }

  private async copyRoomCode(): Promise<void> {
    if (!this.room) return;
    try {
      await navigator.clipboard.writeText(this.room.roomId);
      trackInviteShared('code');
      this.setStatus('Code copied!');
    } catch (err) {
      captureError(err, { scope: 'lobbyScene', action: 'copyRoomCode' });
    }
  }

  private async copyInviteLink(): Promise<void> {
    if (!this.room) return;
    try {
      const url = getJoinUrl(this.room.roomId);
      if (!url) return;
      await navigator.clipboard.writeText(url);
      trackInviteShared('link');
      this.setStatus('Link copied!');
    } catch (err) {
      captureError(err, { scope: 'lobbyScene', action: 'copyInviteLink' });
    }
  }

  private createSeatRows(startY: number): void {
    const rowHeight = 48;
    const rowSpacing = 58;
    const rowCenterX = GAME_WIDTH / 2 - 100;
    const rowWidth = 580;
    const leftEdge = rowCenterX - rowWidth / 2;
    const rightEdge = rowCenterX + rowWidth / 2;

    for (let i = 0; i < 4; i++) {
      const y = startY + i * rowSpacing;

      const rowBg = this.add.graphics();
      this.rowBounds[i] = { x: rowCenterX, y, width: rowWidth, height: rowHeight };
      this.drawRowBackground(i, THEME.colors.borderHex);
      this.rowBackgrounds[i] = rowBg;
      this.mainContainer?.add(rowBg);

      this.rowSlotTexts[i] = this.add.text(leftEdge + 26, y, `${i + 1}`, {
        fontFamily: THEME.fonts.pixelBold,
        fontSize: THEME.sizes.body,
        color: THEME.colors.text,
      }).setOrigin(0, 0.5);
      this.mainContainer?.add(this.rowSlotTexts[i]!);

      this.rowNameTexts[i] = this.add.text(leftEdge + 60, y, '—', {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.bodyLarge,
        color: THEME.colors.text,
      }).setOrigin(0, 0.5);
      this.mainContainer?.add(this.rowNameTexts[i]!);

      this.rowStatusTexts[i] = this.add.text(rightEdge - 20, y, '', {
        fontFamily: THEME.fonts.pixel,
        fontSize: THEME.sizes.small,
        color: THEME.colors.textMuted,
      }).setOrigin(1, 0.5);  // Right-aligned inside row
      this.mainContainer?.add(this.rowStatusTexts[i]!);

      const actionBtn = createSmallButton(this, {
        x: rightEdge + 60,  // Outside the row
        y,
        text: '...',
        onClick: () => this.onSeatAction(i),
      });
      actionBtn.setVisible(false);
      this.rowActionButtons[i] = actionBtn;
      this.mainContainer?.add(actionBtn);
    }
  }

  private drawRowBackground(index: number, strokeColor: number): void {
    const rowBg = this.rowBackgrounds[index];
    const bounds = this.rowBounds[index];
    if (!rowBg || !bounds) return;
    rowBg.clear();
    rowBg.fillStyle(THEME.colors.backgroundLightHex, 0.72);
    rowBg.fillRoundedRect(bounds.x - bounds.width / 2, bounds.y - bounds.height / 2, bounds.width, bounds.height, ROW_CORNER_RADIUS);
    rowBg.lineStyle(2, strokeColor);
    rowBg.strokeRoundedRect(bounds.x - bounds.width / 2, bounds.y - bounds.height / 2, bounds.width, bounds.height, ROW_CORNER_RADIUS);
  }

  private updateUI(): void {
    // Update mode and difficulty button selection states (host)
    this.updateModeButtons();
    this.updateDifficultyButtons();

    // Update guest labels (if guest view)
    if (this.guestModeLabel) {
      this.guestModeLabel.setText(this.roomMode === 'telephone' ? 'TELEPHONE' : 'CO-OP');
    }
    if (this.guestDifficultyLabel) {
      this.guestDifficultyLabel.setText(this.roomDifficulty.toUpperCase());
    }

    // Update seat rows
    let allFilled = true;
    let allGuestsReady = true;

    for (let i = 0; i < 4; i++) {
      const seat = this.seatsByIndex.get(i);
      const visible = i < this.totalPlayers;

      this.rowBackgrounds[i]?.setVisible(visible);
      this.rowSlotTexts[i]?.setVisible(visible);
      this.rowNameTexts[i]?.setVisible(visible);
      this.rowStatusTexts[i]?.setVisible(visible);

      if (!visible) {
        this.rowActionButtons[i]?.setVisible(false);
        continue;
      }

      if (!seat?.hasOwner) {
        allFilled = false;
      }

      const isHost = seat?.ownerSessionId === this.hostSessionId;
      if (!isHost && seat?.hasOwner && !seat.ready) {
        allGuestsReady = false;
      }

      const name = seat?.name || 'Waiting...';
      const status = !seat?.hasOwner ? 'OPEN' : seat.ready ? 'READY' : 'NOT READY';
      const statusColor = seat?.ready ? THEME.colors.cyan : THEME.colors.textMuted;

      this.rowNameTexts[i]?.setText(name);
      this.rowStatusTexts[i]?.setText(status);
      this.rowStatusTexts[i]?.setColor(statusColor);

      // Update row border color
      const borderColor = this.assignedSeat === i
        ? (getLocalSeatColor(i, this.totalPlayers, [this.assignedSeat]) ?? THEME.colors.cyanHex)
        : THEME.colors.borderHex;
      this.drawRowBackground(i, borderColor);

      // Action button (kick for host, edit for self)
      const actionBtn = this.rowActionButtons[i];
      if (actionBtn) {
        const isMySeat = this.assignedSeat === i;
        const canKick = this.role === 'host' && !isHost && seat?.hasOwner;
        const canEdit = isMySeat && !this.isReady;

        actionBtn.setVisible(canKick || canEdit);
        if (canKick) {
          setButtonLabel(actionBtn, 'KICK');
        } else if (canEdit) {
          setButtonLabel(actionBtn, 'EDIT');
        }
      }
    }

    // Update ready button
    if (this.readyButton) {
      setButtonLabel(this.readyButton, this.isReady ? 'CANCEL READY' : 'MARK READY');
    }

    // Update start button
    if (this.startButton) {
      const canStart = allFilled && allGuestsReady;
      setButtonEnabled(this.startButton, canStart);
    }
  }

  private onSeatAction(seatIndex: number): void {
    const seat = this.seatsByIndex.get(seatIndex);
    const isMySeat = this.assignedSeat === seatIndex;
    const isHostSeat = seat?.ownerSessionId === this.hostSessionId;

    if (isMySeat && !this.isReady) {
      this.openEditModal();
    } else if (this.role === 'host' && !isHostSeat && seat?.hasOwner) {
      this.kickSeat(seatIndex);
    }
  }

  private openEditModal(): void {
    if (this.modal) {
      this.modal.destroy();
      this.modal = null;
    }

    const seat = this.assignedSeat !== null ? this.seatsByIndex.get(this.assignedSeat) : null;
    const currentName = seat?.name || this.playerName;

    if (this.isMobile) {
      this.modal = new SimpleFormModal({
        scene: this,
        title: 'EDIT YOUR NAME',
        hint: 'Tap Save when done',
        height: 320,
        fields: [{ label: 'NAME', value: currentName, maxlength: 24, y: 340 }],
        buttons: [
          { text: 'SAVE', style: 'primary', onClick: (values) => this.saveName(values['NAME']), y: 440, x: GAME_WIDTH / 2 - 120 },
          { text: 'CANCEL', style: 'secondary', onClick: () => { this.modal?.destroy(); this.modal = null; }, y: 440, x: GAME_WIDTH / 2 + 170 },
        ],
        onDestroy: () => { this.modal = null; },
      });
    } else {
      this.modal = new BindingFormModal({
        scene: this,
        title: 'EDIT YOUR SETUP',
        hint: 'Update name and control',
        playerName: currentName,
        currentBinding: this.binding,
        onSave: (name, binding) => {
          this.playerName = name;
          this.binding = binding;
          this.room?.send('set_name', { name });
          this.updateBindingDisplay();
        },
        onCancel: () => {},
        onDestroy: () => { this.modal = null; },
      });
    }
  }

  private saveName(name: string): void {
    const trimmed = name.trim() || this.playerName;
    this.playerName = trimmed;
    this.room?.send('set_name', { name: trimmed });
    this.modal?.destroy();
    this.modal = null;
  }

  private setMode(mode: 'coop' | 'telephone'): void {
    debugLog('setMode called', { mode, role: this.role, hasRoom: !!this.room });
    if (this.role !== 'host') return;
    if (!this.room) return;
    // Update local state immediately for responsive UI
    this.roomMode = mode;
    this.updateModeButtons();
    // Send to server
    debugLog('setMode sending to server', { mode });
    this.room.send('set_mode', { mode });
  }

  private toggleReady(): void {
    this.isReady = !this.isReady;
    this.room?.send('set_ready', { ready: this.isReady });

    // Track when player marks ready
    if (this.isReady) {
      trackLobbyReady();
    }

    this.updateUI();
  }

  private kickSeat(seatIndex: number): void {
    if (this.role !== 'host') return;
    this.room?.send('kick_seat', { seat: seatIndex });
  }

  private startMatch(): void {
    if (this.role !== 'host') return;
    this.room?.send('start_match');
  }

  private leaveLobby(): void {
    // Release instance lobby if Discord host is closing the lobby
    if (isDiscordActivity() && this.role === 'host') {
      void releaseInstanceLobby();
    }

    try {
      this.room?.leave(true);
    } catch (err) {
      captureError(err, { scope: 'lobbyScene', action: 'leaveLobby' });
    }
    clearOnlineRoom();
    clearDiscordPresence();
    // Go back to FriendWebPlayScene in web mode, MenuScene in Discord Activity
    this.scene.start(isDiscordActivity() ? 'MenuScene' : 'FriendWebPlayScene');
  }

  private setStatus(text: string): void {
    if (this.statusText) {
      this.statusText.setText(text);
    }
  }

  private showMainContent(): void {
    if (this.isRoomReady) return;
    this.isRoomReady = true;

    // Track lobby view
    if (this.room) {
      trackLobbyView(this.room.roomId, this.roomMode);
    }

    // Hide loading text
    if (this.loadingText) {
      this.loadingText.setVisible(false);
    }

    // Show main container
    if (this.mainContainer) {
      this.mainContainer.setVisible(true);
    }

    // Show QR element (DOM elements can't be in containers)
    if (this.qrElement) {
      this.qrElement.setVisible(true);
    }
  }

  shutdown(): void {
    if (this.modal) {
      this.modal.destroy();
      this.modal = null;
    }
    this.qrElement?.destroy();
    this.qrElement = null;
    if (!this.preserveRoomOnShutdown) {
      this.room?.removeAllListeners?.();
    }
  }
}
